import React, { useRef, useState, useEffect } from "react";
import classes from "./OtpPage.module.css";
import { motion } from "framer-motion";
import {
  FaArrowLeft,
  FaArrowRight,
  FaShieldHalved,
  FaPenToSquare,
  FaRotateRight,
} from "react-icons/fa6";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LinedSection from "../component/LinedSection";
import { verifyOTPApi } from "../http/http";
import { toast } from "react-toastify";

const OTP_LENGTH = 4;
const RESEND_COOLDOWN = 30;

const OtpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mobileNumber = location.state?.mobile || "+241 07XXXXXX";
  const selectedPlan = location.state?.selectedPlan || 1;
  const pack = location.state?.pack || "daily";

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const inputsRef = useRef([]);

  const { t } = useTranslation();

  const isOtpComplete = otp.every((digit) => digit.trim().length === 1);

  // Resend countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleChange = (value, index) => {
    const cleanValue = value.replace(/\D/g, "").slice(-1);

    const updatedOtp = [...otp];
    updatedOtp[index] = cleanValue;
    setOtp(updatedOtp);

    if (cleanValue && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const updatedOtp = [...otp];
        updatedOtp[index] = "";
        setOtp(updatedOtp);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pastedData) return;

    const updatedOtp = Array(OTP_LENGTH).fill("");
    pastedData.split("").forEach((digit, index) => {
      updatedOtp[index] = digit;
    });

    setOtp(updatedOtp);

    const nextIndex = Math.min(pastedData.length, OTP_LENGTH) - 1;
    if (nextIndex >= 0) {
      inputsRef.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOtpComplete || loading) return;

    try {
      setLoading(true);

      const enteredOtp = otp.join("");

      const response = await verifyOTPApi(
        mobileNumber.replace(/\s/g, ""),
        enteredOtp
      );

      const result = response?.data;


      if (result?.message === "OTP Verified successfully") {
        toast.success(result.message);

        navigate("/confirm", {
          state: {
            mobile: mobileNumber,
            selectedPlan,
            pack,
            otp: enteredOtp,

          },
        });
      } else {
        toast.error(result?.message || "OTP verification failed.");
      }
    } catch (error) {
      const result = error?.response?.data;

      if (result?.message === "Invalid OTP entered") {
        toast.error("Invalid OTP entered. Please try again.");
      } else if (result?.message === "Internal server error") {
        toast.error("Internal server error. Please try again later.");
      } else {
        toast.error(
          result?.message || "OTP verification failed. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <main className={classes.main}>
      <div className={classes.glowLayer} />
      <div className={classes.bgGlowOne} />
      <div className={classes.bgGlowTwo} />

      <LinedSection />

      <motion.section
        className={classes.container}
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Back Button */}
        <motion.button
          type="button"
          className={classes.backBtn}
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Retour"
        >
          <FaArrowLeft />
        </motion.button>

        {/* Top Branding */}
        <div className={classes.topLogoWrap}>
          <img
            src="/images/logo2.png"
            alt="MOOVGAMING"
            className={classes.logoImg}
          />
          <div className={classes.brandSub}>
            Powered by <span>MOOV</span>
          </div>
        </div>

        {/* Headings */}
        <div className={classes.headingWrap}>
          <h1 className={classes.title}>
            {t("48")} <span>{t("49")}</span>
          </h1>

          <p className={classes.subtitle}>{t("50")}</p>
        </div>

        {/* Main Glass Card */}
        <form className={classes.card} onSubmit={handleSubmit}>
          {/* Mobile Info Box */}
          <div className={classes.infoBox}>
            <div className={classes.infoLeft}>
              <p className={classes.infoLabel}>
                <span className={classes.infoLabelIcon}><FaShieldHalved /></span>
                {t("51")}
              </p>
              <div className={classes.infoValueWrap}>
                <span className={classes.flagDot} title="Gabon (+241)" />
                <p className={classes.infoValue}>{mobileNumber}</p>
              </div>
            </div>

            <button
              type="button"
              className={classes.editPhoneBtn}
              onClick={() => navigate("/subscribe")}
              title="Modifier le numéro"
            >
              <FaPenToSquare />
              {t("157")}
            </button>
          </div>

          {/* OTP Digits Section */}
          <div className={classes.otpSection}>
            <label className={classes.otpLabel}>
              <span className={classes.labelIcon}><FaShieldHalved /></span>
              {t("158")}
            </label>

            <div className={classes.otpWrap} onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  autoFocus={index === 0}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={`${classes.otpInput} ${digit ? classes.otpInputFilled : ""
                    }`}
                  aria-label={`Chiffre OTP ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            className={`${classes.confirmBtn} ${!isOtpComplete || loading ? classes.confirmBtnDisabled : ""
              }`}
            disabled={!isOtpComplete || loading}
            whileHover={isOtpComplete && !loading ? { scale: 1.02 } : {}}
            whileTap={isOtpComplete && !loading ? { scale: 0.98 } : {}}
          >
            <span>{loading ? t("159") : t("52")}</span>
            {!loading && <FaArrowRight />}
          </motion.button>



          {/* Trust Reassurance Strip */}
          <div className={classes.trustStrip}>
            <div className={classes.trustItem}>
              <span className={classes.trustIcon}><FaShieldHalved /></span>
              <span>{t("47")}</span>
            </div>
            <div className={classes.trustItem}>
              <span>{t("32")}</span>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className={classes.footerText}>
          &copy; UVCANN SOLUTIONS LLP &bull; {t("156")}
        </div>
      </motion.section>
    </main>
  );
};

export default OtpPage;
